import React, { useEffect } from "react";

// Lightweight themed modal for confirmations and warnings. Replaces
// window.confirm so the dialog matches the dark admin palette and can
// hold a richer body (multi-line explanations, file size hints, etc.).
//
// Controlled component — caller owns `open`. Keyboard: Escape cancels,
// Enter confirms when the confirm button is focused. Focus is moved to
// the confirm button on open so Enter just-works.

export default function Modal({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Confirm"}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        display: "grid",
        placeItems: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#0f0f0f",
          color: "#f4ece0",
          border: "1px solid #2a2a2a",
          borderRadius: 6,
          padding: "1.4rem 1.5rem 1.2rem",
          maxWidth: 480,
          width: "100%",
          fontFamily: "system-ui, -apple-system, sans-serif",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
        }}
      >
        {title && (
          <h2
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              marginBottom: "0.7rem",
            }}
          >
            {title}
          </h2>
        )}
        <div
          style={{
            fontSize: "0.88rem",
            lineHeight: 1.5,
            color: "#cfcabd",
            marginBottom: "1.2rem",
          }}
        >
          {children}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.6rem",
          }}
        >
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: "transparent",
                color: "#aaa",
                border: "1px solid #2a2a2a",
                padding: "0.45rem 0.9rem",
                fontSize: "0.85rem",
                borderRadius: 3,
                cursor: "pointer",
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            style={{
              background: destructive ? "#7a2222" : "#f4ece0",
              color: destructive ? "#f4ece0" : "#0c0c0c",
              border: "none",
              padding: "0.45rem 0.9rem",
              fontSize: "0.85rem",
              borderRadius: 3,
              cursor: "pointer",
              fontWeight: 500,
              letterSpacing: "0.03em",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
